import type React from 'react';

import {
  AdornedInput,
  Adornment,
  FieldSet,
  FormField,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
} from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../../components/Widgets/YesAndNoExpansion';
import { INSUFFICIENT_FUNDS_FEE } from '../../../constants/index';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';
import InlineTooltip from '../../../components/Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

// Third iteration of Insufficient Funds fee which adds a radio selection between a flat fee
// and a associated fee.
export default function InsufficientFundsFee_v3({ section, setSectionValues }: Props) {
  function disableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function enableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: true,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function onAssociatedFeeClick(_id: string): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: true,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: '',
    });
  }

  function onFlatFeeClick(_id: string): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: true,
    });
  }

  function onAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT]: value,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE]: true,
    });
  }

  const insufficientFundEnabled = section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED);

  return (
    <FormField
      label={
        <>
          <Label size="lg">
            Will you charge a fee if a payment gets{' '}
            <InlineTooltip
              tooltipBodyText="Reversals are usually caused by checks being returned for insufficient funds or by the tenant stopping or
          canceling a check. A fee charged for reversal of payment is different from a late fee."
            >
              reversed
            </InlineTooltip>
            ?
          </Label>
          <Paragraph marginBottom="sm">
            Reversals are commonly caused by payments being returned for insufficient funds.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion
          yesHandler={enableInsufficientFundsFee}
          noHandler={disableInsufficientFundsFee}
          yesActive={insufficientFundEnabled}
        >
          <FieldSet legend={<Legend>What type of fee will the tenant pay?</Legend>}>
            <LabeledControl
              controlId="LabeledControl-associated-fee"
              label={<Label>Pay actual cost incurred by landlord</Label>}
              disabled={section.isFieldDisabled(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST)}
              control={
                <Radio
                  name="fee"
                  checked={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.ACTUAL_COST)}
                  onChange={onAssociatedFeeClick}
                />
              }
            />
            <LabeledControl
              controlId="LabeledControl-flat-fee"
              label={<Label>Flat fee</Label>}
              control={
                <Radio
                  name="fee"
                  checked={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE)}
                  onChange={onFlatFeeClick}
                />
              }
            />
            <FormField marginLeft="lg">
              <AdornedInput
                leftAdornment={<Adornment>$</Adornment>}
                rightAdornment={<Adornment>flat fee</Adornment>}
                input={
                  <CurrencyInput
                    aria-labelledby="fee-amount"
                    value={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.FLAT_FEE_AMOUNT)}
                    onBlur={onAmountChange}
                    onChange={onAmountChange}
                    placeholder={CURRENCY_INPUT_PLACEHOLDER}
                    onClick={onFlatFeeClick}
                  />
                }
              />
            </FormField>
          </FieldSet>
        </YesAndNoExpansion>
      }
    />
  );
}
